import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { admin } from '../../utils/api';
import { toast } from 'sonner';
import { 
  FileText, Clock, CheckCircle, XCircle, AlertCircle,
  Mail, Phone, Globe, Building2, Search, ChevronRight,
  User, MapPin, Calendar, Eye
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function AdminCharityApplications({ user, onLogout }) {
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ pending: 0, under_review: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [statusFilter]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await admin.charityApplications(statusFilter || null);
      setApplications(response.data.applications || []);
      setStats(response.data.stats || { pending: 0, under_review: 0, approved: 0, rejected: 0 });
    } catch (error) {
      toast.error('Failed to load charity applications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appId) => {
    try {
      setActionLoading(true);
      await admin.approveCharityApplication(appId);
      toast.success('Charity application approved! Welcome email sent.');
      setSelectedApp(null);
      loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      await admin.rejectCharityApplication(selectedApp.id, rejectReason);
      toast.success('Application rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedApp(null);
      loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!filter) return true;
    const searchLower = filter.toLowerCase();
    return (
      app.organization_name?.toLowerCase().includes(searchLower) ||
      app.primary_contact_email?.toLowerCase().includes(searchLower) ||
      app.ein_tax_id?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white shadow-lg">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Charity Applications
          </h2>
          <p className="text-emerald-50 text-lg">Review and manage charity partner applications</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const Icon = config.icon;
            const count = stats[status] || 0;
            const isActive = statusFilter === status;
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(isActive ? '' : status)}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  isActive 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 bg-white hover:border-emerald-300'
                }`}
                data-testid={`filter-${status}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-600">{config.label}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by organization name, email, or EIN..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-10"
                data-testid="search-applications"
              />
            </div>
          </CardContent>
        </Card>

        {/* Applications List */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Applications ({filteredApplications.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading applications...</div>
              ) : filteredApplications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {statusFilter ? `No ${STATUS_CONFIG[statusFilter]?.label.toLowerCase()} applications` : 'No applications found'}
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredApplications.map((app) => {
                    const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    const isSelected = selectedApp?.id === app.id;
                    
                    return (
                      <button
                        key={app.id}
                        onClick={() => setSelectedApp(app)}
                        className={`w-full p-4 rounded-lg border text-left transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                            : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-gray-50'
                        }`}
                        data-testid={`application-${app.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-900 truncate">
                                {app.organization_name}
                              </h4>
                              <Badge className={statusConfig.color} variant="secondary">
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 truncate">{app.primary_contact_email}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Applied: {formatDate(app.created_at)}
                            </p>
                          </div>
                          <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-transform ${
                            isSelected ? 'text-emerald-600 rotate-90' : 'text-gray-400'
                          }`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detail Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-emerald-600" />
                Application Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedApp ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select an application to view details</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Organization Info */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      Organization
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <p className="text-lg font-semibold text-gray-900">{selectedApp.organization_name}</p>
                      <p className="text-sm text-gray-600">EIN: {selectedApp.ein_tax_id}</p>
                      <Badge variant="outline">{selectedApp.category}</Badge>
                      <p className="text-sm text-gray-700 mt-2">{selectedApp.description}</p>
                      {selectedApp.mission_statement && (
                        <p className="text-sm text-gray-600 italic mt-2">
                          "{selectedApp.mission_statement}"
                        </p>
                      )}
                      {selectedApp.website && (
                        <a 
                          href={selectedApp.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-600 hover:underline flex items-center gap-1 mt-2"
                        >
                          <Globe className="w-4 h-4" />
                          {selectedApp.website}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-emerald-600" />
                      Primary Contact
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <p className="font-medium text-gray-900">{selectedApp.primary_contact_name}</p>
                      <a href={`mailto:${selectedApp.primary_contact_email}`} className="text-sm text-emerald-600 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {selectedApp.primary_contact_email}
                      </a>
                      <a href={`tel:${selectedApp.primary_contact_phone}`} className="text-sm text-gray-600 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {selectedApp.primary_contact_phone}
                      </a>
                      {(selectedApp.address || selectedApp.city) && (
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {[selectedApp.address, selectedApp.city, selectedApp.state, selectedApp.zip_code].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                      Timeline
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                      <p><span className="text-gray-500">Applied:</span> {formatDate(selectedApp.created_at)}</p>
                      {selectedApp.reviewed_at && (
                        <p><span className="text-gray-500">Reviewed:</span> {formatDate(selectedApp.reviewed_at)}</p>
                      )}
                      {selectedApp.admin_notes && (
                        <p className="text-gray-600 mt-2 border-t pt-2">
                          <span className="text-gray-500">Notes:</span> {selectedApp.admin_notes}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {(selectedApp.status === 'pending' || selectedApp.status === 'under_review') && (
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        onClick={() => handleApprove(selectedApp.id)}
                        disabled={actionLoading}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        data-testid="approve-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        onClick={() => setShowRejectModal(true)}
                        disabled={actionLoading}
                        variant="destructive"
                        className="flex-1"
                        data-testid="reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}

                  {/* Status Badge for resolved apps */}
                  {(selectedApp.status === 'approved' || selectedApp.status === 'rejected') && (
                    <div className={`p-4 rounded-lg ${
                      selectedApp.status === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`font-medium ${selectedApp.status === 'approved' ? 'text-green-800' : 'text-red-800'}`}>
                        {selectedApp.status === 'approved' ? '✓ Application Approved' : '✗ Application Rejected'}
                      </p>
                      {selectedApp.reviewed_at && (
                        <p className="text-sm text-gray-600 mt-1">
                          on {formatDate(selectedApp.reviewed_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-red-600">Reject Application</CardTitle>
                <CardDescription>
                  Rejecting {selectedApp?.organization_name}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Reason for rejection (optional)
                  </label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    rows={3}
                    data-testid="reject-reason"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectReason('');
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={actionLoading}
                    variant="destructive"
                    className="flex-1"
                    data-testid="confirm-reject-btn"
                  >
                    {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
